<template>
  <div
    class="ubcf-wrap"
    :class="{
      'ubcf-wrap--compact': hideAmount && !wideLayout,
      'ubcf-wrap--wide': wideLayout,
    }"
  >
    <h6 v-if="!hideTitle" class="ubcf-title">
      {{ displayTitle }}
    </h6>

    <div class="ubcf-grid">
      <div class="ubcf-field" v-if="!hideAmount">
        <label class="ubcf-label">{{ $t ? $t("amount") : "Amount" }}</label>
        <input
          class="ubcf-input ubcf-input--disabled"
          type="text"
          disabled
          :value="formattedAmount"
        />
      </div>

      <div
        class="ubcf-field"
        :class="{ 'ubcf-field--full': hideAmount && !wideLayout }"
      >
        <label class="ubcf-label">{{
          remarkLabel || ($t ? $t("comment") : "Comment")
        }}</label>
        <input
          ref="commentInput"
          class="ubcf-input"
          type="text"
          v-model="form.comment"
          :placeholder="remarkPlaceholder || 'Enter transaction comment'"
          autocomplete="off"
          id="comment"
          autofocus
          @click="openCommentKeyboard"
          @focus="openCommentKeyboard"
        />
        <VkeyboardFloat
          :ID="'comment'"
          :onClickFun="
            () => {
              Object.keys(keyboardsVisible).map((x) => {
                if (x == 'comment') {
                  keyboardsVisible[x] = !keyboardsVisible[x];
                } else {
                  keyboardsVisible[x] = false;
                  showNumpad = false;
                }
              });
            }
          "
        />
        <Vkeyboard
          :inputName="'ubcf-comment'"
          :showOrHide="keyboardsVisible.comment"
          :initialValue="form.comment"
          @onKeyEnter="
            (event) => {
              keyboardsVisible.comment = false;
            }
          "
          @changeKeyboardInput="
            (event) => {
              form.comment = event;
            }
          "
          @onClickClose="keyboardsVisible.comment = false"
        />
      </div>

      <!-- Phone Number -->
      <div class="ubcf-field">
        <label class="ubcf-label">Mobile Number</label>
        <div class="ubcf-phone-row">
          <b-dropdown
            class="ubcf-country-dropdown"
            variant="dropdown-curve"
            :text="selectedCountryCode || 'Code'"
          >
            <div class="ubcf-country-search">
              <input
                v-model="countrySearch"
                type="search"
                placeholder="Search Code"
                autocomplete="off"
              />
            </div>
            <b-dropdown-item
              v-for="country in filteredCountries"
              :key="country.country_id"
              href="#"
              @click="selectCountryCode(country)"
            >
              {{ country.country_mob_code }}
            </b-dropdown-item>
          </b-dropdown>
          <input
            class="ubcf-input ubcf-input--phone"
            type="text"
            :value="formattedPhone"
            placeholder="Enter mobile number"
            readonly
            @click="(() => {
              showNumpad = true;
              keyboardsVisible.comment = false;
            }) "
          />
          <button
            v-if="form.phone"
            class="ubcf-phone-clear"
            type="button"
            @click="
              form.phone = '';
              showNumpad = false;
            "
            aria-label="Clear phone"
          >
            ✕
          </button>
        </div>
      </div>

      <!-- Customer Name -->
      <div class="ubcf-field" v-if="showName">
        <label class="ubcf-label">Name</label>
        <input
          class="ubcf-input"
          type="text"
          v-model="form.name"
          placeholder="Enter full name"
          autocomplete="off"
          @click="showNumpad = false"
          id="name"
        />
        <VkeyboardFloat
          :ID="'name'"
          :onClickFun="
            () => {
              Object.keys(keyboardsVisible).map((x) => {
                if (x == 'name') {
                  keyboardsVisible[x] = !keyboardsVisible[x];
                } else {
                  keyboardsVisible[x] = false;
                }
              });
            }
          "
        />
        <Vkeyboard
          :inputName="'name'"
          :showOrHide="keyboardsVisible.name"
          :initialValue="form.name"
          @onKeyEnter="
            (event) => {
              keyboardsVisible.name = false;
            }
          "
          @changeKeyboardInput="
            (event) => {
              form.name = event;
            }
          "
          @onClickClose="keyboardsVisible.name = false"
        />
      </div>
    </div>

    <!-- Number Pad -->
    <transition name="ubcf-slide">
      <div class="ubcf-numpad" v-if="showNumpad">
        <div class="ubcf-numpad-header">
          <span class="ubcf-numpad-preview">
            <span class="ubcf-numpad-code">{{ selectedCountryCode }}</span>
            {{ formattedPhone || "—" }}
          </span>
          <button
            class="ubcf-done-btn"
            type="button"
            @click="isCustomerCheck()"
          >
            Done
          </button>
        </div>
        <div class="ubcf-numpad-grid">
          <button
            v-for="key in numpadKeys"
            :key="key.value"
            class="ubcf-key"
            :class="key.cls"
            type="button"
            @click="handleKey(key.value)"
          >
            {{ key.label }}
          </button>
        </div>
      </div>
    </transition>

    <!-- Actions -->
    <div class="ubcf-actions">
      <button class="ubcf-btn ubcf-btn--reset" type="button" @click="reset">
        Reset
      </button>
      <button
        class="ubcf-btn ubcf-btn--proceed"
        type="button"
        :disabled="!isValid || !allowProceed"
        @click="proceed"
      >
        Proceed
      </button>
    </div>
  </div>
</template>

<script>
import PosService from "../services/PosService";
import MasterService from "@/services/MasterService";
import Vkeyboard from "@/components/Partials/Vkeyboard.vue";
import VkeyboardFloat from "@/components/Partials/keyboardFloatingToggle.vue";
import { CLIENT_NAME } from "@/helper/Role";

export default {
  name: "UnpaidBookingCustomerForm",

  props: {
    paymentModeName: { type: String, default: "Unpaid Booking" },
    formTitle: { type: String, default: "" },
    hideAmount: { type: Boolean, default: false },
    remarkLabel: { type: String, default: "" },
    remarkPlaceholder: { type: String, default: "" },
    finalAmountToPay: { type: [Number, String], default: 0 },
    currCode: { type: String, default: "AED" },
    minFractionDigits: { type: Number, default: 2 },
    countryCode: { type: String, default: "+971" },
    reservationId: { type: [String, Number], default: "" },
    hideTitle: { type: Boolean, default: false },
    wideLayout: { type: Boolean, default: false },
  },
  components: {
    Vkeyboard,
    VkeyboardFloat,
  },

  data() {
    return {
      form: { name: "", phone: "", comment: "" },
      showNumpad: false,
      showName: false,
      allowProceed: true,
      countries: [],
      countrySearch: "",
      selectedCountryCode: this.countryCode,
      keyboardsVisible: {
        name: false,
        phone: false,
        comment: false,
      },
      numpadKeys: [
        { label: "1", value: "1" },
        { label: "2", value: "2" },
        { label: "3", value: "3" },
        { label: "4", value: "4" },
        { label: "5", value: "5" },
        { label: "6", value: "6" },
        { label: "7", value: "7" },
        { label: "8", value: "8" },
        { label: "9", value: "9" },
        { label: "⌫", value: "del", cls: "ubcf-key--del" },
        { label: "0", value: "0" },
        { label: "✕", value: "clear", cls: "ubcf-key--clear" },
      ],
    };
  },

  computed: {
    isNovoClient() {
      return CLIENT_NAME.NOVO == this.CLIENT_SETUP_NAME;
    },
    displayTitle() {
      if (this.formTitle) return this.formTitle;
      return (
        (this.$t ? this.$t("payBy") : "Pay By") + " " + this.paymentModeName
      );
    },
    formattedAmount() {
      return (
        parseFloat(this.finalAmountToPay).toFixed(this.minFractionDigits) +
        " " +
        this.currCode
      );
    },
    formattedPhone() {
      const d = this.form.phone;
      if (!d) return "";
      if (d.length <= 3) return d;
      if (d.length <= 6) return d.slice(0, 3) + " " + d.slice(3);
      return d.slice(0, 3) + " " + d.slice(3, 6) + " " + d.slice(6);
    },
    filteredCountries() {
      const search = this.countrySearch.trim().toLowerCase();
      if (!search) return this.countries;
      return this.countries.filter((country) => {
        return (
          country.country_mob_code &&
          country.country_mob_code.toLowerCase().includes(search)
        );
      });
    },
    isValid() {
      if (!this.form.name) return false;
      return this.form.phone.length >= 7;
    },
  },

  watch: {
    countryCode(value) {
      if (value) this.selectedCountryCode = value;
    },
  },

  mounted() {
    this.getCountries();
    this.$nextTick(() => {
      this.autoOpenCommentKeyboard();
    });
  },

  beforeDestroy() {
    if (this._autoOpenKeyboardTimer) {
      clearTimeout(this._autoOpenKeyboardTimer);
    }
    document.removeEventListener("click", this.handleDocumentClick);
  },

  methods: {
    async getCountries() {
      try {
        const response = await MasterService.GetActiveList({
          tableName: "ms_countries",
          country_is_active: "Y",
        });
        const records =
          response && response.data && response.data.Records
            ? response.data.Records
            : [];
        this.countries = records.filter(
          (country) => country.country_mob_code != null
        );
      } catch (error) {
        this.$toasted.show("Countries not fetched");
      }
    },
    selectCountryCode(country) {
      this.selectedCountryCode = country.country_mob_code;
      this.countrySearch = "";
    },
    autoOpenCommentKeyboard() {
      this._autoOpenKeyboardTimer = setTimeout(() => {
        this.openCommentKeyboard();
        if (this.$refs.commentInput) {
          this.$refs.commentInput.focus();
        }
        document.addEventListener("click", this.handleDocumentClick);
      }, 150);
    },
    openCommentKeyboard() {
      this.showNumpad = false;
      this.keyboardsVisible = {
        name: false,
        phone: false,
        comment: true,
      };
    },
    handleDocumentClick(event) {
      if (!this.keyboardsVisible.comment) return;

      const target = event.target;
      const commentInput = this.$refs.commentInput;
      if (
        (commentInput && commentInput.contains(target)) ||
        target.closest(".vKeyboard-container") ||
        target.closest(".vkeyboardFloatContainer")
      ) {
        return;
      }

      this.keyboardsVisible = {
        ...this.keyboardsVisible,
        comment: false,
      };
    },
    handleKey(value) {
      if (value === "del") {
        this.form.phone = this.form.phone.slice(0, -1);
      } else if (value === "clear") {
        this.form.phone = "";
      } else {
        if (this.form.phone.length < 12) this.form.phone += value;
        if (this.isNovoClient && this.form.phone.length === 8) {
          this.isCustomerCheck();
        }
      }
    },
    onClickInputHandler(event) {
      console.log("INSIDE onClickInputHandler click happpens", event);
      if (
        Object.keys(this.keyboardsVisible).some(
          (x) => this.keyboardsVisible[x] == true
        )
      ) {
        Object.keys(this.keyboardsVisible).map((x) => {
          this.keyboardsVisible[x] = false;
        });
        this.keyboardsVisible[event] = true;
        this.$forceUpdate();
      }
    },
    reset() {
      this.form = { name: "", phone: "", comment: "" };
      this.showNumpad = false;
    },
    async isCustomerCheck() {
      this.showNumpad = false;
      const response = await PosService.unpaidBookingCustomerLogin({
        reservation_id: this.reservationId,
        mobile_number: this.form.phone,
        country_code: this.selectedCountryCode,
        name: this.form.name.trim(),
      });

      const { data } = response;
      if (!data || !data["status"]) {
        this.hideLoading();
        return this.$toasted.show(
          data["message"] || "Error updating customer data"
        );
      }

      if (data["status"] && data["is_add_guest"]) {
        this.showName = true;
        this.allowProceed = true;
        this.hideLoading();
        return;
      }
      console.log({ data });
      if (data["status"] && data["Records"]) {
        this.showName = true;
        this.form.name = `${data["Records"]["first_name"]} ${data["Records"]["last_name"]}`;
      }
    },
    async proceed() {
      if (!this.isValid) {
        return this.$toasted.show("Please fill all required fields");
      }
      try {
        this.showLoading();
        const response = await PosService.unpaidBookingCustomerLogin({
          reservation_id: this.reservationId,
          mobile_number: this.form.phone,
          country_code: this.selectedCountryCode,
          name: this.form.name.trim(),
        });

        const { data } = response;
        if (!data || !data["status"]) {
          this.hideLoading();
          return this.$toasted.show(
            data["message"] || "Error updating customer data"
          );
        }

        if (data["status"] && data["is_add_guest"]) {
          this.showName = true;
          this.hideLoading();
          this.allowProceed = true;
          return;
        }

        let customer_id = null;
        let guestDetails = { guest_id: null, guest_user_id: null };
        if (data["Records"] && data["Records"]["cust_id"]) {
          customer_id = data["Records"]["cust_id"];
        }
        if (data["Records"] && data["Records"]["guest_id"]) {
          guestDetails.guest_id = data["Records"]["guest_id"];
          guestDetails.guest_user_id = data["Records"]["guest_user_id"];
        }

        this.$emit("proceed", {
          customer_id,
          guestDetails,
          country_code: this.selectedCountryCode,
          comment: this.form.comment.trim(),
        });
      } catch (error) {
        console.log(error);
      } finally {
        this.hideLoading();
      }
      // this.$emit("proceed", {
      //   name: this.form.name.trim(),
      //   phone: this.form.phone,
      //   comment: this.form.comment.trim(),
      //   amount: this.finalAmountToPay,
      // });
    },
  },
};
</script>

<style scoped>
/* ── Container ──────────────────────────────────────────── */
.ubcf-wrap {
  padding: 4px 0;
  font-family: inherit;
}

.ubcf-title {
  font-size: 15px;
  font-weight: 600;
  color: #222;
  margin-bottom: 14px;
}

/* ── Two-column grid ────────────────────────────────────── */
.ubcf-grid {
  display: grid;
  grid-template-columns: 1fr 1fr;
  gap: 10px 14px;
  margin-bottom: 12px;
}

.ubcf-wrap--compact .ubcf-grid {
  grid-template-columns: 1fr;
  gap: 16px;
}

.ubcf-wrap--wide .ubcf-grid {
  grid-template-columns: repeat(3, minmax(0, 1fr));
  align-items: end;
  gap: 18px;
  margin-bottom: 16px;
}

/* ── Field ──────────────────────────────────────────────── */
.ubcf-field {
  display: flex;
  flex-direction: column;
}

.ubcf-field--full {
  grid-column: 1 / -1;
}

.ubcf-label {
  font-size: 11px;
  font-weight: 600;
  color: #888;
  text-transform: uppercase;
  letter-spacing: 0.07em;
  margin-bottom: 5px;
}

.ubcf-input {
  border-radius: 10px;
  border: 1.5px solid #e0daf5;
  background: #fff;
  font-size: 14px;
  height: 40px;
  padding: 0 12px;
  color: #222;
  width: 100%;
  outline: none;
  transition: border-color 0.15s, box-shadow 0.15s;
  font-family: inherit;
}

.ubcf-input:focus {
  border-color: #7f77dd;
  box-shadow: 0 0 0 3px rgba(127, 119, 221, 0.14);
}

.ubcf-input--disabled {
  background: #f0eef9;
  color: #534ab7;
  font-weight: 600;
  border-color: #d6d0f5;
  cursor: default;
}

/* ── Phone row ──────────────────────────────────────────── */
.ubcf-phone-row {
  display: flex;
  align-items: center;
  position: relative;
}

.ubcf-country-dropdown {
  flex-shrink: 0;
}

.ubcf-country-dropdown /deep/ .dropdown-toggle {
  height: 40px;
  min-width: 84px;
  padding: 0 10px;
  background: #eeedfe;
  border: 1.5px solid #d6d0f5;
  border-right: none;
  border-radius: 10px 0 0 10px;
  font-size: 13px;
  font-weight: 600;
  color: #534ab7;
  white-space: nowrap;
  user-select: none;
}

.ubcf-country-dropdown /deep/ .dropdown-menu {
  max-height: 260px;
  min-width: 150px;
  overflow-y: auto;
}

.ubcf-country-search {
  position: sticky;
  top: 0;
  z-index: 1;
  padding: 6px 8px;
  background: #fff;
}

.ubcf-country-search input {
  width: 100%;
  height: 32px;
  padding: 0 8px;
  border: 1px solid #d6d0f5;
  border-radius: 6px;
  outline: none;
}

.ubcf-country-search input:focus {
  border-color: #7f77dd;
}

.ubcf-input--phone {
  border-radius: 0 10px 10px 0 !important;
  cursor: pointer;
  padding-right: 28px !important;
  flex: 1;
}

.ubcf-phone-clear {
  position: absolute;
  right: 8px;
  background: none;
  border: none;
  font-size: 12px;
  color: #bbb;
  cursor: pointer;
  padding: 4px;
  line-height: 1;
}
.ubcf-phone-clear:hover {
  color: #999;
}

/* ── Number Pad ─────────────────────────────────────────── */
.ubcf-numpad {
  background: #f7f6fe;
  border: 1.5px solid #e0daf5;
  border-radius: 14px;
  padding: 12px;
  margin-bottom: 12px;
}

.ubcf-wrap--compact .ubcf-numpad {
  padding: 8px;
  margin-bottom: 10px;
  border-radius: 10px;
}

.ubcf-wrap--compact .ubcf-numpad-header {
  margin-bottom: 6px;
}

.ubcf-wrap--compact .ubcf-numpad-preview {
  font-size: 16px;
}

.ubcf-wrap--compact .ubcf-done-btn {
  padding: 4px 14px;
  font-size: 12px;
}

.ubcf-wrap--compact .ubcf-numpad-grid {
  gap: 5px;
}

.ubcf-wrap--compact .ubcf-key {
  padding: 7px 0;
  border-radius: 7px;
  font-size: 16px;
}

.ubcf-wrap--wide .ubcf-numpad {
  width: 100%;
  max-width: 380px;
  padding: 12px;
  border-radius: 14px;
  margin: 0 auto 12px;
}

.ubcf-wrap--wide .ubcf-numpad-header {
  margin-bottom: 10px;
}

.ubcf-wrap--wide .ubcf-numpad-grid {
  grid-template-columns: repeat(3, 1fr);
  gap: 8px;
}

.ubcf-wrap--wide .ubcf-key {
  min-height: 44px;
  padding: 10px 0;
  border-radius: 10px;
  font-size: 19px;
}

.ubcf-wrap--wide .ubcf-actions {
  justify-content: flex-end;
}

.ubcf-numpad-header {
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: 10px;
  padding: 0 2px;
}

.ubcf-numpad-preview {
  font-size: 20px;
  font-weight: 600;
  color: #3c3489;
  letter-spacing: 0.05em;
}

.ubcf-numpad-code {
  color: #9b94e0;
  margin-right: 4px;
}

.ubcf-done-btn {
  background: #7f77dd;
  color: #fff;
  border: none;
  border-radius: 8px;
  padding: 5px 16px;
  font-size: 13px;
  font-weight: 600;
  cursor: pointer;
  font-family: inherit;
}
.ubcf-done-btn:hover {
  background: #6b63cc;
}

.ubcf-numpad-grid {
  display: grid;
  grid-template-columns: repeat(3, 1fr);
  gap: 8px;
}

.ubcf-key {
  background: #fff;
  border: 1.5px solid #e0daf5;
  border-radius: 10px;
  padding: 14px 0;
  font-size: 20px;
  font-weight: 500;
  color: #222;
  cursor: pointer;
  font-family: inherit;
  transition: background 0.1s, transform 0.08s;
  user-select: none;
}
.ubcf-key:active {
  background: #eeedfe;
  transform: scale(0.95);
}

.ubcf-key--del {
  background: #fdf4f4;
  border-color: #f5c6c6;
  color: #c0392b;
}
.ubcf-key--del:active {
  background: #fce8e8;
}

.ubcf-key--clear {
  background: #f0eef9;
  border-color: #d6d0f5;
  color: #7f77dd;
}
.ubcf-key--clear:active {
  background: #e5e2f8;
}

/* ── Transition ─────────────────────────────────────────── */
.ubcf-slide-enter-active,
.ubcf-slide-leave-active {
  transition: opacity 0.18s ease, transform 0.18s ease;
}
.ubcf-slide-enter,
.ubcf-slide-leave-to {
  opacity: 0;
  transform: translateY(-6px);
}

/* ── Action buttons ─────────────────────────────────────── */
.ubcf-actions {
  display: flex;
  justify-content: center;
  gap: 10px;
  margin-top: 4px;
}

.ubcf-btn {
  border: none;
  border-radius: 10px;
  font-size: 14px;
  font-weight: 600;
  letter-spacing: 0.06em;
  padding: 10px 36px;
  cursor: pointer;
  font-family: inherit;
  transition: opacity 0.15s, transform 0.1s;
}
.ubcf-btn:active {
  transform: scale(0.97);
}

.ubcf-btn--proceed {
  background: #7f77dd;
  color: #fff;
}
.ubcf-btn--proceed:disabled {
  background: #c4c0ed;
  cursor: not-allowed;
  transform: none;
}
.ubcf-btn--proceed:not(:disabled):hover {
  background: #6b63cc;
}

.ubcf-btn--reset {
  background: #f0eef9;
  color: #534ab7;
  border: 1.5px solid #d6d0f5;
}
.ubcf-btn--reset:hover {
  background: #e5e2f8;
}

@media (max-width: 850px) {
  .ubcf-wrap--wide .ubcf-grid {
    grid-template-columns: 1fr;
    gap: 12px;
  }

  .ubcf-wrap--wide .ubcf-numpad-grid {
    grid-template-columns: repeat(3, 1fr);
  }
}
</style>



// WEBPACK FOOTER //
// src/components/UnpaidBookingCheckout.vue